import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';
import TemplateSelector from '@/components/TemplateSelector';
import InterestSection from '@/components/InterestSection';
import { calculateInterest, calculateTotalAmount } from '@/utils/interestCalculator';
import { getSupportedCurrencies } from '@/utils/currency';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
export default function CreateAgreementPage() {
  const { api, user } = useAuth();
  const { t, getTemplateLabel, getTemplateFieldLabel, getTemplateName, getTemplateDescription } = useLanguage();
  const navigate = useNavigate();
  const currencies = getSupportedCurrencies();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    agreement_template: 'LOAN',
    principal_amount: '',
    interest_enabled: true,
    interest_rate: '',
    interest_frequency: 'MONTHLY',
    minimum_interest_months: 3,
    confirmation_mode: 'VIDEO',
    currency: user?.currency || 'INR',
    start_date: new Date().toISOString().split('T')[0],
    duration_months: '',
    notes: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const calculatedInterest = useMemo(() => {
    if (!form.interest_enabled || !form.principal_amount || !form.interest_rate || !form.duration_months) {
      return 0;
    }
    return calculateInterest(
      parseFloat(form.principal_amount),
      parseFloat(form.interest_rate),
      parseInt(form.duration_months),
      form.interest_frequency
    );
  }, [form.interest_enabled, form.principal_amount, form.interest_rate, form.duration_months, form.interest_frequency]);

  const totalAmount = useMemo(() => {
    if (!form.principal_amount) return 0;
    const principal = parseFloat(form.principal_amount);
    return form.interest_enabled ? principal + calculatedInterest : principal;
  }, [form.principal_amount, form.interest_enabled, calculatedInterest]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!form.principal_amount || !form.duration_months) {
      toast.error(t('errors.required_field'));
      return;
    }

    // Validate interest fields if enabled
    if (form.interest_enabled && !form.interest_rate) {
      toast.error(t('errors.required_field'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        agreement_template: form.agreement_template,
        principal_amount: parseFloat(form.principal_amount),
        start_date: form.start_date,
        duration_months: parseInt(form.duration_months),
        notes: form.notes,
        interest_enabled: form.interest_enabled,
        confirmation_mode: form.confirmation_mode,
        currency: form.currency,
      };

      // Only include interest fields if enabled
      if (form.interest_enabled) {
        payload.interest_rate = parseFloat(form.interest_rate);
        payload.interest_frequency = form.interest_frequency;
        payload.minimum_interest_months = parseInt(form.minimum_interest_months) || 0;
      }

      const res = await api.post('/agreements', payload);
      toast.success(t('loan.create.success'));
      navigate(`/agreements/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.server_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="create-agreement-page">
      {/* Header */}
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold tracking-tight">{getTemplateName(form.agreement_template)}</h1>
            <p className="text-muted-foreground text-sm">{getTemplateDescription(form.agreement_template)}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selection */}
        <div className="bg-white border border-border p-6 space-y-5">
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{t('templates.select_template')}</h3>

          <TemplateSelector
            value={form.agreement_template}
            onChange={(value) => handleChange('agreement_template', value)}
          />
        </div>

        {/* Agreement Terms */}
        <div className="bg-white border border-border p-6 space-y-5">
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{t('loan.create.terms_title') || 'Agreement Terms'}</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{getTemplateFieldLabel(form.agreement_template, 'principal_amount')} *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">{currencies.find(c => c.code === (user?.currency || 'INR'))?.symbol || '$'}</span>
                <Input
                  data-testid="principal-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={getTemplateFieldLabel(form.agreement_template, 'principal_placeholder')}
                  value={form.principal_amount}
                  onChange={e => handleChange('principal_amount', e.target.value)}
                  className="pl-8 h-12 text-lg rounded-sm"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{getTemplateFieldLabel(form.agreement_template, 'duration')} *</label>
              <Input
                data-testid="duration-input"
                type="number"
                min="1"
                max="360"
                placeholder={getTemplateFieldLabel(form.agreement_template, 'duration_placeholder')}
                value={form.duration_months}
                onChange={e => handleChange('duration_months', e.target.value)}
                className="h-12 text-lg rounded-sm"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('loan.create.start_date_label')} *</label>
            <Input
              data-testid="start-date-input"
              type="date"
              value={form.start_date}
              onChange={e => handleChange('start_date', e.target.value)}
              className="h-12 rounded-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{getTemplateFieldLabel(form.agreement_template, 'notes_label')}</label>
            <textarea
              data-testid="notes-input"
              rows={3}
              placeholder={getTemplateFieldLabel(form.agreement_template, 'notes_placeholder')}
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              className="w-full rounded-sm border border-input bg-white px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
            />
        </div>
        </div>
        
        {/* Confirmation & Currency Config */}
        <div className="bg-white border border-border p-6 space-y-5">
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Currency</label>
              <Select value={form.currency} onValueChange={val => handleChange('currency', val)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.symbol} ({c.code}) - {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Confirmation Mode</label>
              <RadioGroup value={form.confirmation_mode} onValueChange={val => handleChange('confirmation_mode', val)} className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="VIDEO" id="video_mode" />
                  <Label htmlFor="video_mode">Video (Default)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="TEXT" id="text_mode" />
                  <Label htmlFor="text_mode">Simple Text</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </div>

        {/* Interest Configuration */}
        <InterestSection
          template={form.agreement_template}
          enabled={form.interest_enabled}
          onEnabledChange={(enabled) => handleChange('interest_enabled', enabled)}
          rate={form.interest_rate}
          onRateChange={(rate) => handleChange('interest_rate', rate)}
          frequency={form.interest_frequency}
          onFrequencyChange={(frequency) => handleChange('interest_frequency', frequency)}
          minimumInterestMonths={form.minimum_interest_months}
          onMinimumInterestMonthsChange={(months) => handleChange('minimum_interest_months', months)}
          principal={form.principal_amount ? parseFloat(form.principal_amount) : 0}
          duration={form.duration_months ? parseInt(form.duration_months) : 0}
          currency={form.currency}
        />

        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {t('loan.create.role_notice', { 
              principal: getTemplateLabel(form.agreement_template, 'principal'),
              secondary: getTemplateLabel(form.agreement_template, 'secondary')
            })}
          </p>
          <Button
            type="submit"
            data-testid="create-agreement-submit"
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-sm uppercase tracking-wider font-semibold text-xs"
          >
            {loading ? t('common.loading') : getTemplateFieldLabel(form.agreement_template, 'create_button')}
          </Button>
        </div>
      </form>
    </div>
  );
}
